// src/utils/qzConfig.js
import { getQZ } from '../utils/qzCompat';

let _securityInit = false;
let _csrfToken = null;

// Adjust if you serve the cert from another origin/path
const CERT_URL = '/qz/digital-certificate.txt';
// Your server signer (already mounted via app.use('/api', qz))
const SIGN_URL = '/api/qz';

async function getCsrfToken(force = false) {
  if (_csrfToken && !force) return _csrfToken;
  // Your app exposes a CSRF endpoint under /api (see app.js)
  const r = await fetch('/api/csrf-token', { credentials: 'include' });
  const j = await r.json();
  _csrfToken = j.csrfToken;
  return _csrfToken;
}

export async function configureQZSecurity() {
  if (_securityInit) return;
  const qz = await getQZ();

  // Ensure QZ uses a standard Promise factory
  if (qz.api && typeof qz.api.setPromiseType === 'function') {
    qz.api.setPromiseType(function (resolver) { return new Promise(resolver); });
  }

  // --- SIGNED MODE (persistent trust) ---
  // 1) Provide the public certificate
  qz.security.setCertificatePromise(function (resolve, reject) {
    fetch(CERT_URL, { cache: 'no-store' })
      .then(r => r.ok ? r.text() : r.text().then(Promise.reject))
      .then(resolve, reject);
  });

  // 2) Sign every request server-side (SHA-512)
  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise(function (toSign) {
    return async function (resolve, reject) {
      const signOnce = async (forceCsrf) => {
        const csrf = await getCsrfToken(forceCsrf);
        return fetch(SIGN_URL, {
          method: 'POST',
          credentials: 'include', // send session cookie
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrf     // required by your CSRF middleware
          },
          body: JSON.stringify({ request: toSign })
        });
      };
      try {
        let resp = await signOnce(false);
        // Kiosk οθόνες μένουν ανοιχτές για μέρες: σε 403 το cached CSRF token
        // είναι μπαγιάτικο — φρέσκο token και μία επανάληψη.
        if (resp.status === 403) resp = await signOnce(true);
        const txt = await resp.text();
        if (!resp.ok) throw new Error(txt || 'Signing failed');
        resolve(txt);
      } catch (e) {
        reject(e);
      }
    };
  });

  _securityInit = true;
}

export async function ensureQZConnection() {
  // ΠΑΝΤΑ πρώτα το security setup: χωρίς αυτό η σύνδεση γίνεται ΑΝΥΠΟΓΡΑΦΗ και
  // το QZ Tray δείχνει «anonymous request / untrusted website» σε κάθε οθόνη
  // (αυτό ακριβώς συνέβαινε με τον poller του print queue, που συνδεόταν
  // χωρίς να έχει περάσει από το printSticker).
  await configureQZSecurity();
  const qz = await getQZ();
  if (qz.websocket.isActive()) return;
  await qz.websocket.connect({ retries: 2, delay: 500 });
}

export async function getPrinterConfig(printerName) {
  const qz = await getQZ();
  await ensureQZConnection();
  return qz.configs.create(printerName || null, {
    copies: 1,
    encoding: 'UTF-8'
  });
}

export async function findPrinterByName(hint) {
  const qz = await getQZ();
  await ensureQZConnection();
  const list = await qz.printers.find();
  const wanted = (hint || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const tokens = wanted.split(/\s+/).filter(Boolean);
  const score = (p) => {
    const s = p.toLowerCase();
    let sc = 0;
    for (const t of tokens) if (s.includes(t)) sc++;
    if (/(zebra|zdesigner|zd220)/.test(s)) sc += 2;
    return sc;
  };
  let best = null, bestScore = -1;
  for (const p of list) {
    const sc = score(p);
    if (sc > bestScore) { best = p; bestScore = sc; }
  }
  return best;
}
